/**
 * Application entrypoint.
 *
 * Bootstrap order:
 *   1. Initialize a minimal Sentry SDK so any failure during step 2 still
 *      surfaces as telemetry (without this, a broken ConfigMap or unreachable
 *      backend manifests as a silent blank page with zero observability).
 *   2. Fetch `/config.json` (runtime config; written into the bundle for
 *      preview environments via a ConfigMap, served from `public/` for local
 *      development).
 *   3. Dynamic-import the rest of the app so module-level reads of the
 *      runtime config (notably `src/constants/API/apiURL.ts`) see a fully
 *      populated singleton.
 *
 * This unblocks single-image preview deploys: the same nginx bundle now
 * works against any per-preview API URL without rebuilding.
 */

import * as Sentry from '@sentry/react'
import { initSentryEarly } from 'utils/analytics/sentryEarly'
import { installSafeDomMutations } from 'utils/dom/safeDomMutations'
import { loadRuntimeConfig } from 'constants/API/runtimeConfig'

// One-line localized bootstrap-failure messages. i18next isn't loaded yet at
// this point in the bootstrap (the translation bundles ship with the rest of
// the app, which we dynamic-import below) — so we keep a tiny inline map and
// pick the user's language from `navigator.language`.
const BOOTSTRAP_ERROR_MESSAGES = {
  en: { title: 'Application failed to load', body: 'The runtime configuration could not be fetched. Please try refreshing the page.' },
  de: { title: 'Anwendung konnte nicht geladen werden', body: 'Die Laufzeitkonfiguration konnte nicht abgerufen werden. Bitte aktualisieren Sie die Seite.' },
  fr: { title: 'Échec du chargement de l’application', body: 'La configuration d’exécution n’a pas pu être récupérée. Veuillez actualiser la page.' },
  it: { title: 'Impossibile caricare l’applicazione', body: 'La configurazione runtime non può essere recuperata. Aggiorna la pagina.' },
  nl: { title: 'Applicatie kon niet worden geladen', body: 'De runtimeconfiguratie kon niet worden opgehaald. Vernieuw de pagina.' },
  es: { title: 'No se pudo cargar la aplicación', body: 'No se pudo obtener la configuración de tiempo de ejecución. Actualice la página.' },
} as const

const pickBootstrapErrorMessage = (): { title: string; body: string } => {
  const lang = (navigator.language || 'en').slice(0, 2).toLowerCase()
  return BOOTSTRAP_ERROR_MESSAGES[lang as keyof typeof BOOTSTRAP_ERROR_MESSAGES] ?? BOOTSTRAP_ERROR_MESSAGES.en
}

initSentryEarly()

// Guard React's DOM reconciliation against the browser-auto-translation
// `removeChild` NotFoundError before anything mounts (ISSUE-2153).
installSafeDomMutations()

async function bootstrap(): Promise<void> {
  await loadRuntimeConfig()
  // Dynamic import — runs only after the runtime config singleton is
  // populated, so any module that reads from it at import time is safe.
  await import('./bootstrap')
}

bootstrap().catch((error) => {
  // Capture to Sentry first (the SDK was initialized synchronously above) so
  // a broken preview shows up in telemetry rather than being invisible.
  Sentry.captureException(error)
  console.error('Failed to bootstrap application:', error)

  const { title, body } = pickBootstrapErrorMessage()
  const root = document.getElementById('root')
  if (root) {
    root.innerHTML =
      '<div style="font-family: sans-serif; padding: 2rem;">' +
      `<h1>${title}</h1>` +
      `<p>${body}</p>` +
      '</div>'
  }
})
